// domain/model/Credential.js

/**
 * A certification or license, for the period it was valid.
 *
 * IT HOLDS ITS OWN WINDOW, and that is what makes it a different shape from the effective-dated
 * records elsewhere in the platform. A funding source version is a RESTATEMENT: it runs until the
 * next version starts, so its end is derived and never stored. A credential is a REPLACEMENT that
 * arrives with both dates already printed on the certificate, so a renewal is a new record carrying
 * its own window rather than an amendment of the previous one, and the same certificate number
 * appears more than once down the list. The versioned form dialog is therefore the wrong tool here:
 * there is no "from when" to ask about.
 *
 * THE START DATE IS THE POINT OF THIS RECORD. Without it the only question answerable is "is this
 * valid now", and every question worth asking is about a date of service in the past: what did this
 * person hold the day they delivered the session. An expiry on its own cannot answer that.
 *
 * NO EXPIRY IS A REAL ANSWER, not a missing one. Some credentials do not expire, which is why the
 * list has always been able to say so, and an absent end means open-ended rather than unknown.
 *
 * STANDING IS A SEPARATE QUESTION FROM THE WINDOW, and both have to be good. An issuer can suspend
 * or revoke what it granted, and a person can let one go inactive without waiting for it to lapse:
 * the dates still read as open while the credential may not be practiced on. The state sheet's rule
 * that a lapsed license stops somebody practising from the next day applies the same way to one that
 * was taken away, so a credential is usable only when the date falls inside its window AND nothing
 * has been done to it.
 *
 * THIS IS NOT THE FIELD THAT WAS REMOVED. The old `status` carried `active`, `inactive` and
 * `expired` together, and `expired` was the problem: it restated what the dates already said, so it
 * went stale the day a certificate lapsed and nobody reopened the record. Expiry is derived here and
 * never stored. What remains is the half no date can answer — what the ISSUER has done.
 *
 * THE LAST VALID DAY IS THE EXPIRY ITSELF. The state sheet puts it plainly: a lapsed license means
 * the holder may not practice from the NEXT day, so the window includes its end date.
 *
 * @domainObject value-object
 * @typedef {Object} Credential
 * @property {string|null} organization - who issues it, as the clinic's credentialing config names it
 * @property {string|null} level - what it is: RBT, BCBA, BCaBA, a state license
 * @property {string|null} certificateId - the number on the certificate; shared across renewals
 * @property {string|null} jurisdiction - the state that issued it, where the issuer is a state
 * @property {string|null} startDate - 'YYYY-MM-DD', the first day it was valid
 * @property {string|null} expirationDate - 'YYYY-MM-DD', the last day; null means it does not expire
 * @property {string|null} status - the issuer's standing; absent means nothing has been done to it
 * @property {CredentialVerification|null} verification
 */

/**
 * What an issuer has done to a credential it granted.
 *
 * `expired` is deliberately NOT here: it is what the dates say, and storing it as well is how the two
 * come to disagree. A record carrying the legacy value still reads as good standing, because its
 * window answers the question independently and no old record is silently made unbillable by it.
 */
export const CREDENTIAL_STANDING = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
  REVOKED: 'revoked',
};

/**
 * Standings under which a credential may not be practiced on.
 *
 * Named for the exceptions rather than for good standing, so the ordinary case is the absent value
 * and nobody has to set anything on a credential that is simply fine.
 */
const BLOCKING_STANDINGS = [
  CREDENTIAL_STANDING.INACTIVE,
  CREDENTIAL_STANDING.SUSPENDED,
  CREDENTIAL_STANDING.REVOKED,
];

/** Has nothing been done to this credential by whoever granted it? */
export const isInGoodStanding = (credential) => !BLOCKING_STANDINGS.includes(credential?.status);

/**
 * @typedef {Object} CredentialVerification
 * @property {string|null} verifiedOn - 'YYYY-MM-DD'
 * @property {string|null} method - how it was checked: a registry, the issuing board
 */

const text = (value) => (typeof value === 'string' && value.trim().length > 0 ? value.trim() : null);

/** Reads a stored entry as a Credential. Dates arrive already normalized to 'YYYY-MM-DD' strings. */
export function createCredential(entry) {
  if (entry === null || entry === undefined) {
    return null;
  }

  const verification = entry.verification ?? null;

  return {
    organization: text(entry.organization),
    level: text(entry.level),
    certificateId: text(entry.certificateId),
    jurisdiction: text(entry.jurisdiction),
    status: text(entry.status),
    startDate: text(entry.startDate),
    expirationDate: text(entry.expirationDate),
    verification: verification === null ? null : {
      verifiedOn: text(verification.verifiedOn),
      method: text(verification.method),
    },
  };
} // createCredential()

/**
 * Can this record answer what was held on a given day?
 *
 * Only with a start date. Every credential stored before this record existed carries an expiry and
 * no start, so this is how those are told apart from the ones that can be billed on, and it is what
 * the editor marks incomplete.
 */
export const isResolvable = (credential) => typeof credential?.startDate === 'string';

/**
 * Was this credential valid on `asOf`?
 *
 * An unresolvable credential answers NO rather than yes. The alternative reads an undated row as
 * valid for all of history, which is the direction that bills work nobody can show was permitted.
 */
export function isValidOn(credential, asOf) {
  if (!isResolvable(credential) || typeof asOf !== 'string' || asOf.length === 0) {
    return false;
  }

  // A suspended or revoked credential reads as open on the calendar and may not be practiced on.
  if (!isInGoodStanding(credential)) {
    return false;
  }

  if (asOf < credential.startDate) {
    return false;
  }

  // Absent in EITHER form means open-ended. A record that came through `createCredential` carries an
  // explicit null, but the legacy readers hand raw stored objects straight in and a stored row need
  // not have the key at all — and `asOf <= undefined` is false, which would read a credential that
  // never expires as invalid on every date. The backend mirror already accepted both.
  const doesNotExpire = credential.expirationDate === null || credential.expirationDate === undefined;

  return doesNotExpire || asOf <= credential.expirationDate;
} // isValidOn()

/** Every credential valid on a date. More than one is ordinary: people hold several at once. */
export function validCredentialsOn(credentials, asOf) {
  return (credentials ?? []).filter((credential) => isValidOn(credential, asOf));
} // validCredentialsOn()

/**
 * Do two records describe the same credential rather than two different ones?
 *
 * Identity is the issuer, the state where there is one, and the number, never the number alone. Two
 * organizations can issue the same string, and so can two states: a behavior-analyst license is
 * granted per state and one person can hold several at once, so treating them as one certificate
 * would report a license in a second state as a renewal overlapping the first.
 *
 * This is what makes a renewal recognizable as one, and what overlap is checked within.
 */
export function isSameCredential(left, right) {
  return left?.organization === right?.organization
    && (left?.jurisdiction ?? null) === (right?.jurisdiction ?? null)
    && left?.certificateId !== null
    && left?.certificateId === right?.certificateId;
} // isSameCredential()

/**
 * Do two windows for the same credential overlap?
 *
 * The rule that replaces the stored "one active per organization" flag. A renewal starts the day
 * after the previous one ends, so touching windows are fine and only a genuine overlap is wrong:
 * one credential cannot be valid twice over the same day.
 */
export function windowsOverlap(left, right) {
  if (!isResolvable(left) || !isResolvable(right)) {
    return false;
  }

  const leftEndsBefore = left.expirationDate !== null && left.expirationDate < right.startDate;
  const rightEndsBefore = right.expirationDate !== null && right.expirationDate < left.startDate;

  return !leftEndsBefore && !rightEndsBefore;
} // windowsOverlap()

/**
 * The credential to read for a person on a date, or null.
 *
 * ONE RULE, PUBLISHED, because it has two callers that must not disagree: the staff-list columns and
 * the tokens printed on notes and reports. They had the same rule written twice and drifted at the
 * first edit, so an undated suspended credential showed as current in one and was omitted by the
 * other for the same record.
 *
 * THE FALLBACK IS FOR UNDATED RECORDS ONLY, and only those the old system marked active. A record
 * from before start dates existed cannot be resolved at all, so it is shown rather than dropped;
 * that is not licence to read a DATED record outside its window, which is what resolving as of a
 * date exists to prevent.
 *
 * An undated record marked `expired` is not shown, even though `isInGoodStanding` admits that value.
 * The two are not in tension: that value is ignored where a window can answer instead, and an undated
 * record has no window, so the marker is the only thing anybody said about it.
 */
export function resolveCredentialOn(credentials, asOf) {
  const undatedAndActive = (credentials ?? []).filter((credential) => (
    !isResolvable(credential) && credential?.status === CREDENTIAL_STANDING.ACTIVE));

  return validCredentialsOn(credentials, asOf)[0] ?? undatedAndActive[0] ?? null;
} // resolveCredentialOn()

/**
 * The soonest date one of this person's qualifications lapses, or null.
 *
 * WHAT IT IS FOR. Firestore cannot range-query a field inside an array of maps, so "everyone
 * expiring in the next 60 days" cannot be asked of the credential list at all. A single date beside
 * it turns that into an indexed query. It is a HINT FOR A QUERY and never the answer: the query
 * narrows to a handful of people and their own records say what actually lapses.
 *
 * ONE WINDOW PER CERTIFICATE, THE LATEST. A renewal is a new record, so a certificate renewed twice
 * has three windows and only the last of them is still ahead. Taking the earliest date across every
 * window would pick the first, expired years ago, and pin that person to the top of the list
 * permanently however diligently they renew.
 *
 * SUPERSEDED IS NOT THE SAME AS LAPSED, and this is why the rule is per certificate rather than "the
 * earliest one still valid". A certificate that ran out and was never renewed has a latest window
 * whose expiry is in the PAST, and that is the case the whole field exists for: they may not practice
 * and nobody noticed. Filtering to what is currently valid would hide exactly that person.
 *
 * A QUERY OVER IT MUST CARRY A LOWER BOUND. Firestore orders null before every string, so
 * `where('nextCredentialExpiry', '<=', <date>)` matches every staff member whose value is null —
 * which is everybody holding nothing that expires, the exact people the question is not about. The
 * job that asks "who lapses in the next 60 days" needs `>= <today>` as well, and one asking who has
 * already lapsed needs a bound of its own rather than relying on the upper one alone.
 *
 * IT TAKES NO DATE, deliberately. Nothing here depends on when it is computed, so the value cannot
 * go stale between the write that produced it and the read that uses it — which matters, because it
 * is written by an editor and read by a job that runs on some other day.
 *
 * @param {Array<Object>} credentials
 * @returns {string|null} 'YYYY-MM-DD', or null when nothing this person holds expires
 */
export function nextCredentialExpiry(credentials) {
  const usable = (credentials ?? []).filter((credential) => (
    isResolvable(credential) && isInGoodStanding(credential)));

  const latestPerCertificate = usable.reduce((latest, credential) => {
    const existing = latest.find((entry) => isSameCredential(entry, credential));

    if (existing === undefined) {
      return latest.concat([credential]);
    }

    // A record with no number matches nothing, so it stands alone and is its own latest.
    return existing.startDate >= credential.startDate
      ? latest
      : latest.map((entry) => (entry === existing ? credential : entry));
  }, []);

  const expiries = latestPerCertificate
    .map((credential) => credential.expirationDate)
    .filter((date) => typeof date === 'string');

  return expiries.length === 0 ? null : expiries.sort()[0];
} // nextCredentialExpiry()
